import { forwardRef } from "react";
import clsx from "clsx";
import { SalesOrderResponseType } from "constants/types/salesOrder";
import { formatCurrency, formatDateFormal } from "utils/helpers/format";
import s from "./SalesOrderPrintTemplate.module.css";

/**
 * Điều khoản in cố định trên mọi phiếu — không phải ghi chú riêng của đơn.
 * Đây là ràng buộc trách nhiệm khi giao hàng nên phải xuất hiện kể cả khi
 * người lập đơn không nhập ghi chú nào.
 */
const DELIVERY_DISCLAIMER =
  "Để đảm bảo quyền lợi của quý khách hàng, chúng tôi đề nghị quý khách hàng " +
  "phải kiểm tra, xác nhận hàng hóa và hình thức thanh toán. Sau khi việc giao " +
  "hàng đã hoàn tất, chúng tôi không chịu trách nhiệm nếu hàng hóa bị bẹp méo " +
  "và đổ vỡ.";

const InfoRow = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <div className={s.infoRow}>
      <span className={s.infoLabel}>{label}</span>
      <span className={s.infoValue}>{value}</span>
    </div>
  ) : null;

interface Props {
  salesOrder: SalesOrderResponseType;
}

const SalesOrderPrintTemplate = forwardRef<HTMLDivElement, Props>(
  ({ salesOrder }, ref) => {
    const {
      partner,
      warehouse,
      createdBy,
      items = [],
      notes,
      oldDebt,
    } = salesOrder;

    const displayDate = salesOrder.confirmedAt
      ? formatDateFormal(salesOrder.confirmedAt)
      : formatDateFormal(salesOrder.createdAt);

    const totalDebt = Number(oldDebt) || 0;
    const grandTotal = Number(salesOrder.totalAmount) + totalDebt;

    return (
      <div ref={ref} className={s.page}>
        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className={s.headerSection}>
          <div className={s.headerTitle}>PHIẾU GIAO NHẬN HÀNG HÓA</div>
          <div className={s.headerDate}>{displayDate}</div>
        </div>

        {/* ── Partner info ────────────────────────────────────────────────────── */}
        <div className={s.infoSection}>
          <div className={s.infoRow}>
            <span className={s.infoLabel}>Đơn vị mua hàng</span>
            <span className={s.infoValue}>{partner.name}</span>
          </div>
          <InfoRow label="Điện thoại" value={partner.phone} />
          <InfoRow label="Địa chỉ" value={partner.address} />
          <InfoRow label="Mã số thuế" value={partner.taxCode} />
          <InfoRow label="Số Hóa Đơn" value={salesOrder.code} />
          <InfoRow
            label="Người phụ trách"
            value={
              salesOrder.contactPerson?.fullname ??
              partner.contactPerson?.fullname
            }
          />
        </div>

        {/* ── Items table ─────────────────────────────────────────────────────── */}
        <table className={s.table}>
          <thead>
            <tr className={s.tableHeaderRow}>
              <th className={clsx(s.th, s.colStt)}>STT</th>
              <th className={clsx(s.th, s.colCode)}>Mã mặt hàng</th>
              <th className={s.th}>Tên mặt hàng</th>
              <th className={clsx(s.th, s.colUnit)}>Đơn vị</th>
              <th className={clsx(s.th, s.colQty)}>Số lượng</th>
              <th className={clsx(s.th, s.colPrice)}>Đơn giá</th>
              <th className={clsx(s.th, s.colTotal)}>Tổng tiền</th>
            </tr>
          </thead>

          <tbody>
            {items.map((item, index) => (
              <tr key={item.id}>
                <td className={clsx(s.td, s.colStt)}>{index + 1}</td>
                <td className={clsx(s.td, s.colCode)}>
                  {item.product?.code ?? ""}
                </td>
                <td className={s.td}>
                  {item.productName}
                  {item.productSpec && (
                    <>
                      <br />
                      <span className={s.productSpec}>{item.productSpec}</span>
                    </>
                  )}
                </td>
                <td className={clsx(s.td, s.colUnit)}>{item.productUnit}</td>
                <td className={clsx(s.td, s.colQty)}>
                  {item.quantity.toLocaleString("vi-VN")}
                </td>
                <td className={clsx(s.td, s.colPrice)}>
                  {formatCurrency(item.actualPrice)}
                </td>
                <td className={clsx(s.td, s.colTotal)}>
                  {formatCurrency(item.totalAmount)}
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr>
              <td colSpan={6} className={s.summaryLabel}>
                Cộng tiền hàng / Net Sales total:
              </td>
              <td className={s.summaryValue}>
                {formatCurrency(salesOrder.totalAmount)}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* ── Totals ──────────────────────────────────────────────────────────── */}
        <div className={s.totalsSection}>
          <div className={s.totalRow}>
            <span className={s.totalLabel}>Công nợ cũ:</span>
            <span className={s.totalValue}>
              {totalDebt > 0 ? formatCurrency(totalDebt) : ""}
            </span>
          </div>
          <div className={s.totalRow}>
            <span className={s.totalLabel}>Tổng tiền cần thu:</span>
            <span className={s.totalValue}>{formatCurrency(grandTotal)}</span>
          </div>
        </div>

        {/* ── Notes ───────────────────────────────────────────────────────────── */}
        <div className={s.notesSection}>
          <div className={s.notesTitle}>Ghi chú</div>
          {notes && <div className={s.notesText}>{notes}</div>}
          <div className={clsx(s.notesText, notes && s.notesDisclaimer)}>
            {DELIVERY_DISCLAIMER}
          </div>
        </div>

        {/* ── Signatures ──────────────────────────────────────────────────────── */}
        <div className={s.signatureSection}>
          <div className={s.signatureCol}>
            <div className={s.signatureTitle}>Khách hàng / Customer</div>
            <span className={s.signatureSubtitle}>Ký, họ tên</span>
          </div>

          <div className={s.signatureCol}>
            <div className={s.signatureTitle}>Người lập / Prepared</div>
            <span className={s.signatureSubtitle}>Ký, họ tên</span>
            <span className={s.signatureName}>{createdBy.fullname}</span>
          </div>

          <div className={s.signatureCol}>
            <div className={s.signatureTitle}>Thủ kho ({warehouse.name})</div>
            <span className={s.signatureSubtitle}>Ký, họ tên</span>
          </div>
        </div>
      </div>
    );
  },
);

SalesOrderPrintTemplate.displayName = "SalesOrderPrintTemplate";

export default SalesOrderPrintTemplate;
